import { DefaultButton, Icon, Stack } from "@fluentui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { DefaultCardTitle } from "../DisplayContractInfo/styles";

import { ContractShipmentsContent } from "./ContractShipmentsContent";
import { ContractShipmentCancellationDialog } from "./dialogs/ContractShipmentCancellationDialog";
import { ContractShipmentDeliveryDialog } from "./dialogs/ContractShipmentDeliveryDialog";
import { ContractShipmentFormDialog } from "./dialogs/ContractShipmentFormDialog";

import { Card } from "@/components/Shared/Card";
import { useGet } from "@/core/libs/api/react-query/hook/use-get";
import { useApi } from "@/core/libs/api/react-query/useApi";
import {
  type ContractShipmentAction,
  type ListContractShipmentsResponseItem,
  type CreateContractShipmentRequest,
  type ListContractShipmentsParams,
  type ListContractShipmentsResponse,
  type UpdateContractShipmentRequest,
  type UpdateContractShipmentStatusRequest,
  type UpdateContractShipmentStatusResponse
} from "@/interfaces/modules/contract-shipments";
import { CONTRACTS_QUERY_TOKEN } from "@/modules/Contracts/constants";

interface ContractShipmentsProps {
  contractId: ListContractShipmentsParams["contractId"];
  canCreate: boolean;
  canUpdate: boolean;
  canListCosts: boolean;
}

interface StatusMessages {
  success: string;
  error: string;
}

export function ContractShipments({
  contractId,
  canCreate,
  canUpdate,
  canListCosts
}: ContractShipmentsProps): JSX.Element {
  const { execute } = useApi();

  const queryClient = useQueryClient();
  const queryToken = `contract-shipments-${contractId}`;

  const { data: contractShipments, isFetching } =
    useGet<ListContractShipmentsResponse>({
      url: `contract-shipments/contract/${contractId}`,
      queryToken,
      params: null,
      errorMessage: "Não foi possível carregar os envios."
    });

  const [isLoading, setIsLoading] = useState(false);
  const [activeAction, setActiveAction] = useState<ContractShipmentAction>();
  const [selectedContractShipment, setSelectedContractShipment] =
    useState<ListContractShipmentsResponseItem>();

  async function refreshContractShipments() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [queryToken] }),
      queryClient.invalidateQueries({ queryKey: [CONTRACTS_QUERY_TOKEN] })
    ]);
  }

  function openDialog(
    action: ContractShipmentAction,
    shipment?: ListContractShipmentsResponseItem
  ) {
    setSelectedContractShipment(shipment);
    setActiveAction(action);
  }

  function closeDialog() {
    if (isLoading) return;

    setActiveAction(undefined);
    setSelectedContractShipment(undefined);
  }

  async function createContractShipment(
    data: CreateContractShipmentRequest
  ): Promise<boolean> {
    const response = await execute({
      url: `contract-shipments/contract/${contractId}`,
      method: "POST",
      bodyObj: data,
      showAlert: true,
      messages: {
        success: "Envio salvo com sucesso.",
        error: "Não foi possível salvar o envio."
      },
      setIsFetching: setIsLoading
    });

    if (!response.response?.ok) return false;

    await refreshContractShipments();

    return true;
  }

  async function updateContractShipment(
    data: UpdateContractShipmentRequest
  ): Promise<boolean> {
    if (!selectedContractShipment) return false;

    const response = await execute({
      url: `contract-shipments/${selectedContractShipment.id}`,
      method: "PATCH",
      bodyObj: data,
      showAlert: true,
      messages: {
        success: "Envio salvo com sucesso.",
        error: "Não foi possível salvar o envio."
      },
      setIsFetching: setIsLoading
    });

    if (!response.response?.ok) return false;

    await refreshContractShipments();

    return true;
  }

  async function updateContractShipmentStatus(
    contractShipmentId: number,
    data: UpdateContractShipmentStatusRequest,
    messages: StatusMessages
  ): Promise<boolean> {
    const response = await execute<UpdateContractShipmentStatusResponse>({
      url: `contract-shipments/${contractShipmentId}/status`,
      method: "PATCH",
      bodyObj: data,
      showAlert: true,
      messages,
      setIsFetching: setIsLoading
    });

    if (!response.response?.ok) return false;

    await refreshContractShipments();

    return true;
  }

  return (
    <Stack>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <DefaultCardTitle>
          <Icon iconName="DeliveryTruck" style={{ marginRight: 10 }} />
          Envios
        </DefaultCardTitle>

        {canCreate && (
          <DefaultButton
            iconProps={{ iconName: "Add" }}
            text="Adicionar envio"
            onClick={() => openDialog("CREATE")}
            disabled={isFetching || isLoading}
          />
        )}
      </Stack>

      <Card borderRadius="1rem" padding="1rem">
        <ContractShipmentsContent
          contractShipments={contractShipments ?? []}
          canUpdate={canUpdate}
          canListCosts={canListCosts}
          isLoading={isFetching || isLoading}
          openDialog={openDialog}
        />
      </Card>

      {(activeAction === "CREATE" || activeAction === "EDIT") && (
        <ContractShipmentFormDialog
          shipment={
            activeAction === "EDIT" ? selectedContractShipment : undefined
          }
          canListCosts={canListCosts}
          isSaving={isLoading}
          onDismiss={closeDialog}
          onSave={
            activeAction === "EDIT"
              ? updateContractShipment
              : createContractShipment
          }
        />
      )}

      {activeAction === "CONFIRM_DELIVERY" && selectedContractShipment && (
        <ContractShipmentDeliveryDialog
          shipment={selectedContractShipment}
          isSaving={isLoading}
          onDismiss={closeDialog}
          onUpdateStatus={updateContractShipmentStatus}
        />
      )}

      {activeAction === "CANCEL" && selectedContractShipment && (
        <ContractShipmentCancellationDialog
          shipment={selectedContractShipment}
          isSaving={isLoading}
          onDismiss={closeDialog}
          onUpdateStatus={updateContractShipmentStatus}
        />
      )}
    </Stack>
  );
}
